import { Dumbbell, FlaskConical, Gift, Mail, TicketPercent, Trophy, UserCog, Users, UsersRound } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import Layout from '../../components/Layout'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { Toggle } from '../../components/ui/toggle'
import { useUser } from '../../contexts/UserContext'
import CrmManagement from './CrmManagement'
import DiscountManagement from './DiscountManagement'
import GroupManagement from './GroupManagement'
import PlayerManagement from './PlayerManagement'
import PrizeManagement from './PrizeManagement'
import SportsManagement from './SportsManagement'
import TournamentManagement from './TournamentManagement'
import UserManagement from './UserManagement'

// Beta features toggle. Currently inert because there's nothing gated behind
// it — pool play (the prior beta gate) was promoted to production. The
// toggle stays so the plumbing is ready when the next beta lands; the
// disabled state + copy avoids confusion.
function BetaFeaturesToggle() {
  const { user, betaFeaturesEnabled } = useUser()
  if (user?.role !== 'admin') return null

  return (
    <div
      className="flex items-center gap-2 text-sm"
      title="No current beta features available for testing."
    >
      <FlaskConical className="h-4 w-4 text-muted-foreground" />
      <span className="text-muted-foreground">Beta</span>
      <Toggle checked={betaFeaturesEnabled} disabled />
      <span className="text-xs text-muted-foreground hidden sm:inline">
        No current beta features available for testing
      </span>
    </div>
  )
}

const TABS = [
  { value: 'users',       label: 'Users',       icon: UserCog,    Component: UserManagement },
  { value: 'tournaments', label: 'Tournaments', icon: Trophy,     Component: TournamentManagement },
  { value: 'players',     label: 'Players',     icon: Users,      Component: PlayerManagement },
  { value: 'sports',      label: 'Sports',      icon: Dumbbell,   Component: SportsManagement },
  { value: 'groups',      label: 'Groups',      icon: UsersRound, Component: GroupManagement },
  { value: 'prizes',      label: 'Prizes',      icon: Gift,       Component: PrizeManagement },
  { value: 'discounts',   label: 'Discounts',   icon: TicketPercent, Component: DiscountManagement },
  { value: 'crm',         label: 'CRM',         icon: Mail,       Component: CrmManagement },
]

export default function AdminDashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const active = TABS.find(t => t.value === tabParam)?.value || 'tournaments'

  const onTabChange = (value) => {
    const params = new URLSearchParams(searchParams)
    if (value === 'tournaments') params.delete('tab')
    else params.set('tab', value)
    setSearchParams(params, { replace: true })
  }

  const activeTab = TABS.find(t => t.value === active) || TABS[1]
  const ActiveComponent = activeTab.Component
  const ActiveIcon = activeTab.icon

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
          <BetaFeaturesToggle />
        </div>
        <div className="mb-2 max-w-xs">
          <label htmlFor="admin-section" className="sr-only">Admin section</label>
          <Select value={active} onValueChange={onTabChange}>
            <SelectTrigger id="admin-section" className="w-full">
              <SelectValue>
                <span className="inline-flex items-center gap-2">
                  <ActiveIcon className="h-4 w-4" />
                  {activeTab.label}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {TABS.map(({ value, label, icon: Icon }) => (
                <SelectItem key={value} value={value}>
                  <span className="inline-flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <ActiveComponent embedded />
    </Layout>
  )
}
